// UI Constants - Centralized configuration for consistent styling and behavior

// Animation & Timing
export const ANIMATION_DURATIONS = {
  FAST: 200,
  MEDIUM: 300,
  SLOW: 400,
  DEBOUNCE_DEFAULT: 2000,
  PROGRESS_STEP: 40,
  PROGRESS_START_DELAY: 200, // Small delay before progress animation starts
  TOOLTIP_DELAY: 300,
} as const

// Layout & Sizing
export const LAYOUT = {
  CONTAINER_MAX_WIDTH: 'max-w-4xl',
  MIN_HEIGHT: '400px',
  BORDER_RADIUS: {
    SMALL: 'rounded-lg',
    MEDIUM: 'rounded-xl',
    LARGE: 'rounded-2xl',
    FULL: 'rounded-full',
  },
} as const

// Component Dimensions
export const DIMENSIONS = {
  TEXTAREA_MIN_ROWS: 5,
  TEXTAREA_MAX_ROWS: 8,
  BUTTON_HEIGHT: {
    SMALL: 'h-8',
    MEDIUM: 'h-10',
    LARGE: 'h-12',
  },
  BUTTON_WIDTH: {
    SMALL: 'w-16',
    MEDIUM: 'w-20',
    LARGE: 'w-24',
    EXTRA_LARGE: 'w-32',
  },
  MIN_STRUCTURED_HEIGHT: 'min-h-[120px]',
  MIN_EMPTY_STATE_HEIGHT: 'min-h-[120px]',
} as const

// Form Constants
export const FORM = {
  MAX_REFINEMENT_ROUNDS: 5,
  PLACEHOLDER_CONVERSATION: `Friend: Ok, you might have been right
You: Of course I was.  about what?
Friend: When you said I shouldn't get bangs...
`,
  PLACEHOLDER_HINT: 'I told you so, as a gif (optional)',
  HINT_PREFIX: 'Hint:',
} as const

// Messages & Labels
export const MESSAGES = {
  PROCESSING: 'Processing conversation...',
  EMPTY_STRUCTURED_VIEW: 'Enter conversation text in input to see results here',
  SEARCH_MODE_HINT: 'Search mode - Enter keywords below',
  ERROR_PREFIX: 'Error processing text - click to clear',
  TOOLTIP_RAW_TEXT: 'Raw Text - Enter conversations as plain text',
  TOOLTIP_PROCESSED_TEXT: 'Processed Text - Edit structured conversation turns',
  TOOLTIP_PROCESSING: 'Processing text...',
} as const

// Button Labels & Icons
export const BUTTON_LABELS = {
  INPUT: 'Input',
  PROCESSED: 'Processed',
  PROCESSING: 'Processing',
  ERROR: 'Error',
  RATING: 'Filter',
} as const

// Explicit Rating Options
export const EXPLICIT_RATINGS = {
  G: 'G',
  PG: 'PG',
  PG_13: 'PG-13',
  R: 'R',
} as const

export const RATING_LABELS = {
  [EXPLICIT_RATINGS.G]: 'G - All Ages',
  [EXPLICIT_RATINGS.PG]: 'Safe For Work',
  [EXPLICIT_RATINGS.PG_13]: 'A Little Risky',
  [EXPLICIT_RATINGS.R]: 'No Filter',
} as const

// Visible ratings for the UI (excludes G rating)
export const VISIBLE_RATINGS = [
  EXPLICIT_RATINGS.PG,
  EXPLICIT_RATINGS.PG_13,
  EXPLICIT_RATINGS.R,
] as const

// CSS Classes - Common patterns
export const CSS_CLASSES = {
  GLASSMORPHISM_BASE: 'glass-card',
  GRADIENT_BACKGROUND: 'var(--ai-gradient-bright)',
  TEXT_MUTED: 'text-default-500',
  TEXT_ERROR: 'text-red-600',
  TRANSITION_COLORS: 'transition-colors duration-200',
  BUTTON_ICON_SIZE: 'w-3 h-3',
  LARGE_ICON_SIZE: 'w-5 h-5',
  SEARCH_ICON_SIZE: 'w-8 h-8',
} as const

// Z-Index layers
export const Z_INDEX = {
  BACKGROUND: 'z-0',
  CONTENT: 'z-10',
  INDICATOR: 'z-10',
  MODAL: 'z-50',
} as const
